import { useEffect, useState } from "react";
import Button from "../forms/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import styles from "./StandardModal.module.css";
import Tooltip from "../Tooltip";
import { getLocalStorageItem, setLocalStorageItem } from "@/utils/localStorageUtils";
import Checkbox from "../forms/Checkbox/Checkbox";

export interface StandardModalProps {
  icon: ({size}: { size: number }) => React.ReactNode;
  title: string;
  body: React.ReactNode;
  open?: boolean;
  submitText?: string;
  submitSentiment?: "primary" | "danger";
  altSubmitText?: string;
  submitDisabledReason?: string | null;
  rememberDecisionKey?: string;
  onClose?: () => void;
  onSubmit?: () => void | Promise<void>;
  onAltSubmit?: () => void | Promise<void>;
}

export default function StandardModal({
  title,
  open,
  body,
  submitText = "Submit",
  submitSentiment = "primary",
  altSubmitText,
  submitDisabledReason,
  rememberDecisionKey,
  onClose,
  onSubmit,
  onAltSubmit,
  icon,
}: StandardModalProps) {
  const Icon = icon;
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const handleLoading = (f?: () => void | Promise<void>) => async () => {
    setLoading(true);
    await f?.();
    setLoading(false);
  }

  const rememberDecision = rememberDecisionKey
    ? getLocalStorageItem<boolean>(rememberDecisionKey, false)
    : false;

  useEffect(() => {
    if (open && rememberDecision) {
      onSubmit();
    } else {
      setShowModal(open);
    }
  }, [open]);

  return (
    <Modal open={showModal} onClose={onClose} className={styles.modal} closeOnOutsideClick={false}>
      <ModalHeader marginRight="0" divider={false}>
        <div className={styles.header}>
          <div className={styles.icon}><Icon size={40}/></div>
          <h1 className={styles.heading}>{title}</h1>
        </div>
      </ModalHeader>
      <ModalBody className={styles.body}>
        {body}
      </ModalBody>
      <ModalFooter className={styles.footer}>
        {rememberDecisionKey && (
          <label className={styles.dontShowAgain}>
            <Checkbox onChange={(value) => setLocalStorageItem(rememberDecisionKey, value)} />
            <span>Don&apos;t show again</span>
          </label>
        )}
        <Button
          disabled={loading}
          style={{flex: 1}}
          type="secondary"
          size="large"
          onClick={() => onClose?.()}
        >
          Cancel
        </Button>
        {altSubmitText && <Button
          disabled={loading}
          disableWhileOnClick={true}
          style={{flex: 1}}
          type="black"
          size="large"
          onClick={handleLoading(onAltSubmit)}
        >
          {altSubmitText}
        </Button>}
        <Tooltip title={submitDisabledReason} delayMs={0}>
          <Button
            disabled={loading || !!submitDisabledReason}
            disableWhileOnClick={true}
            style={{flex: 1}}
            type={submitSentiment}
            size="large"
            onClick={handleLoading(onSubmit)}
          >
            {submitText}
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  )
}
